import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";
import { getAIExhibit21Copy } from "../content/aiExhibit21Enhancements";

const BASE_POINTS = Object.freeze([
  { id: "a", x1: 0.95, x2: 0.90, label: 1, color: "blue", shape: "circle" },
  { id: "b", x1: 0.88, x2: 0.78, label: 1, color: "blue", shape: "circle" },
  { id: "c", x1: 0.92, x2: 0.68, label: 1, color: "blue", shape: "circle" },
  { id: "d", x1: 0.08, x2: 0.15, label: 0, color: "red", shape: "triangle" },
  { id: "e", x1: 0.12, x2: 0.28, label: 0, color: "red", shape: "diamond" },
  { id: "f", x1: 0.20, x2: 0.10, label: 0, color: "red", shape: "square" },
]);

const TEST_POINT = Object.freeze({
  id: "test",
  x1: 0.90,
  x2: 0.80,
  color: "blue",
  shape: "circle",
});

const SURPRISE_POINT = Object.freeze({
  id: "surprise",
  x1: 0.90,
  x2: 0.08,
  label: 0,
  color: "blue",
  shape: "triangle",
});

const LEARNING_RATE = 2.0;

const BLUE = "#60A5FA";
const RED = "#FB7185";
const SHAPE_COLOR = "#F8FAFC";

const sigmoid = (z) => 1 / (1 + Math.exp(-z));
const clamp = (value, min, max) =>
  Math.max(min, Math.min(max, value));

function probability(model, point) {
  return sigmoid(
    model.w1 * point.x1 +
      model.w2 * point.x2 +
      model.b
  );
}

function trainOne(model, points) {
  let gradW1 = 0;
  let gradW2 = 0;
  let gradB = 0;

  points.forEach((point) => {
    const p = probability(model, point);
    const error = p - point.label;

    gradW1 += error * point.x1;
    gradW2 += error * point.x2;
    gradB += error;
  });

  const n = points.length;

  return {
    w1:
      model.w1 -
      LEARNING_RATE * (gradW1 / n),
    w2:
      model.w2 -
      LEARNING_RATE * (gradW2 / n),
    b:
      model.b -
      LEARNING_RATE * (gradB / n),
    step: model.step + 1,
  };
}

function getMetrics(model, points) {
  let loss = 0;
  let correct = 0;

  points.forEach((point) => {
    const p = clamp(
      probability(model, point),
      1e-6,
      1 - 1e-6
    );

    loss += -(
      point.label * Math.log(p) +
      (1 - point.label) * Math.log(1 - p)
    );

    if (
      (p >= 0.5 ? 1 : 0) === point.label
    ) {
      correct += 1;
    }
  });

  return {
    loss: loss / points.length,
    accuracy: correct / points.length,
  };
}

function machineStatement(p, t21) {
  if (Math.abs(p - 0.5) < 0.02) {
    return {
      title: t21.untrainedTitle,
      body: t21.untrainedBody,
    };
  }

  const predictsZorb = p >= 0.5;
  const confidence = predictsZorb
    ? p
    : 1 - p;

  if (confidence >= 0.78) {
    return {
      title: predictsZorb
        ? t21.confidentZorb
        : t21.confidentNot,
      body: t21.simpleStory,
    };
  }

  return {
    title: predictsZorb
      ? t21.leaningZorb
      : t21.leaningNot,
    body: t21.simpleStory,
  };
}

function ShapeMark({
  shape,
  size = 26,
}) {
  if (shape === "triangle") {
    return (
      <View
        style={{
          width: 0,
          height: 0,
          borderLeftWidth: size * 0.48,
          borderRightWidth: size * 0.48,
          borderBottomWidth: size * 0.88,
          borderLeftColor: "transparent",
          borderRightColor: "transparent",
          borderBottomColor: SHAPE_COLOR,
        }}
      />
    );
  }

  if (shape === "diamond") {
    return (
      <View
        style={{
          width: size * 0.72,
          height: size * 0.72,
          borderWidth: 3,
          borderColor: SHAPE_COLOR,
          backgroundColor: "transparent",
          transform: [{ rotateZ: "45deg" }],
        }}
      />
    );
  }

  if (shape === "square") {
    return (
      <View
        style={{
          width: size * 0.72,
          height: size * 0.72,
          borderWidth: 3,
          borderColor: SHAPE_COLOR,
          backgroundColor: "transparent",
        }}
      />
    );
  }

  return (
    <View
      style={{
        width: size * 0.72,
        height: size * 0.72,
        borderRadius: 999,
        borderWidth: 3,
        borderColor: SHAPE_COLOR,
        backgroundColor: "transparent",
      }}
    />
  );
}

function CreatureVisual({
  point,
  size = 62,
}) {
  const dotColor =
    point.color === "red" ? RED : BLUE;

  return (
    <View
      style={{
        width: size * 1.65,
        minHeight: size,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: Math.max(8, size * 0.16),
      }}
    >
      <View
        style={{
          width: size * 0.56,
          height: size * 0.56,
          borderRadius: 999,
          backgroundColor: dotColor,
          borderWidth: 2,
          borderColor: "#DCEEFF",
        }}
      />

      <View
        style={{
          width: size * 0.56,
          height: size * 0.56,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ShapeMark
          shape={point.shape}
          size={size * 0.52}
        />
      </View>
    </View>
  );
}

function ParameterRow({
  label,
  value,
}) {
  return (
    <View style={{ marginTop: 11 }}>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <Text
          style={{
            color: C.text,
            fontSize: 14,
            fontWeight: "800",
          }}
        >
          {label}
        </Text>

        <Text
          style={{
            color: C.accent,
            fontSize: 14,
            fontWeight: "900",
          }}
        >
          {value.toFixed(3)}
        </Text>
      </View>

      <View
        style={{
          height: 8,
          marginTop: 5,
          borderRadius: 999,
          backgroundColor: C.surface,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            width: `${
              clamp(
                Math.abs(value) / 4,
                0.025,
                1
              ) * 100
            }%`,
            height: "100%",
            borderRadius: 999,
            backgroundColor:
              value >= 0
                ? C.accent
                : C.warning,
          }}
        />
      </View>
    </View>
  );
}

function ConfidenceBar({
  label,
  value,
  accent = C.accent,
}) {
  return (
    <View style={{ marginTop: 10 }}>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Text
          style={{
            color: C.text,
            fontSize: 16,
            fontWeight: "900",
          }}
        >
          {label}
        </Text>

        <Text
          style={{
            color: C.text,
            fontSize: 16,
            fontWeight: "900",
          }}
        >
          {Math.round(value * 100)}%
        </Text>
      </View>

      <View
        style={{
          height: 14,
          marginTop: 7,
          borderRadius: 999,
          backgroundColor: C.surface,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            width: `${value * 100}%`,
            height: "100%",
            borderRadius: 999,
            backgroundColor: accent,
          }}
        />
      </View>
    </View>
  );
}

function CreatureCard({
  point,
  label,
  highlight = false,
}) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 145,
        padding: 14,
        borderRadius: 15,
        borderWidth: highlight ? 3 : 1,
        borderColor: highlight
          ? C.warning
          : C.border,
        backgroundColor: highlight
          ? C.accentSoft
          : C.surface,
        alignItems: "center",
      }}
    >
      <CreatureVisual
        point={point}
        size={50}
      />

      <Text
        style={{
          color:
            point.label === 1
              ? C.accent
              : C.text,
          fontSize: 13,
          fontWeight: "900",
          marginTop: 7,
          textAlign: "center",
        }}
      >
        {label}
      </Text>
    </View>
  );
}

export default function TrainMachineScreen({
  navigate,
  copy,
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t21 =
    getAIExhibit21Copy(language)
      .trainMachine;

  const [model, setModel] =
    useState({
      w1: 0,
      w2: 0,
      b: 0,
      step: 0,
    });

  const [showInside, setShowInside] =
    useState(false);

  const [
    surpriseVisible,
    setSurpriseVisible,
  ] = useState(false);

  const [
    surpriseAdded,
    setSurpriseAdded,
  ] = useState(false);

  const activePoints = useMemo(
    () =>
      surpriseAdded
        ? [
            ...BASE_POINTS,
            SURPRISE_POINT,
          ]
        : BASE_POINTS,
    [surpriseAdded]
  );

  const metrics = useMemo(
    () =>
      getMetrics(
        model,
        activePoints
      ),
    [model, activePoints]
  );

  const testP = useMemo(
    () =>
      probability(
        model,
        TEST_POINT
      ),
    [model]
  );

  const surpriseP = useMemo(
    () =>
      probability(
        model,
        SURPRISE_POINT
      ),
    [model]
  );

  const statement =
    machineStatement(
      testP,
      t21
    );

  const train = (count) => {
    let next = model;

    for (
      let i = 0;
      i < count;
      i += 1
    ) {
      next = trainOne(
        next,
        activePoints
      );
    }

    setModel(next);

    const wasLearned =
      surpriseAdded &&
      model.step >= 10 &&
      surpriseP < 0.5;
    const isLearned =
      surpriseAdded &&
      next.step >= 10 &&
      probability(
        next,
        SURPRISE_POINT
      ) < 0.5;

    if (!wasLearned && isLearned) {
      playNarration(
        "train_machine_learned_from_surprise"
      );
    }
  };

  const reset = () => {
    setModel({
      w1: 0,
      w2: 0,
      b: 0,
      step: 0,
    });
    setShowInside(false);
    setSurpriseVisible(false);
    setSurpriseAdded(false);
  };

  const surprisePredictsZorb =
    surpriseP >= 0.5;

  return (
    <View style={{ padding: 28 }}>
      <Text
        style={{
          color: C.accent,
          fontSize: 13,
          fontWeight: "900",
          letterSpacing: 1.2,
        }}
      >
        {copy.trainMachine.eyebrow}
      </Text>

      <Text
        style={{
          color: C.text,
          fontSize: 44,
          lineHeight: 50,
          fontWeight: "900",
          marginTop: 8,
        }}
      >
        {copy.trainMachine.title}
      </Text>

      <Text
        style={{
          color: C.muted,
          fontSize: 18,
          lineHeight: 27,
          marginTop: 8,
          maxWidth: 950,
        }}
      >
        {language === "es"
          ? "Primero observa los ejemplos. Después mira cómo la máquina pasa de adivinar a aprender una regla—y cómo un nuevo ejemplo puede demostrar que esa regla era demasiado simple."
          : "First look at the examples. Then watch the machine move from guessing toward a learned rule—and see how one new example can reveal that the rule was too simple."}
      </Text>

      <View
        style={{
          marginTop: 24,
          padding: 20,
          borderRadius: 18,
          borderWidth: 1,
          borderColor: C.border,
          backgroundColor:
            C.surfaceRaised,
        }}
      >
        <Text
          style={{
            color: C.text,
            fontSize: 19,
            fontWeight: "900",
          }}
        >
          {t21.labelsTitle}
        </Text>

        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 10,
            marginTop: 14,
          }}
        >
          {BASE_POINTS.map(
            (point) => (
              <CreatureCard
                key={point.id}
                point={point}
                label={
                  point.label
                    ? copy
                        .trainMachine
                        .zorb
                    : copy
                        .trainMachine
                        .notZorb
                }
              />
            )
          )}
        </View>

        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 16,
            marginTop: 14,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 7,
            }}
          >
            <View
              style={{
                width: 16,
                height: 16,
                borderRadius: 999,
                backgroundColor:
                  BLUE,
                borderWidth: 1,
                borderColor:
                  "#DCEEFF",
              }}
            />
            <Text
              style={{
                color: C.muted,
                fontSize: 14,
              }}
            >
              {t21.featureA}
            </Text>
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 7,
            }}
          >
            <ShapeMark
              shape="circle"
              size={20}
            />
            <Text
              style={{
                color: C.muted,
                fontSize: 14,
              }}
            >
              {t21.featureB}
            </Text>
          </View>
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "flex-start",
          gap: 18,
          marginTop: 18,
        }}
      >
        <AIExhibitNarrationPressable
          audioEnabled={audioEnabled}
          onPress={() => playNarration("train_machine_prediction_story")}
          style={{
            flex: 1,
            minWidth: 350,
            padding: 22,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: C.accent,
            backgroundColor:
              C.accentSoft,
          }}
        >
          <Text
            style={{
              color: C.accent,
              fontSize: 13,
              fontWeight: "900",
              letterSpacing: 0.8,
            }}
          >
            {t21.bigQuestion.toUpperCase()}
          </Text>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 18,
              marginTop: 16,
              flexWrap: "wrap",
            }}
          >
            <View
              style={{
                width: 130,
                minHeight: 110,
                borderRadius: 18,
                borderWidth: 3,
                borderColor:
                  C.success,
                backgroundColor:
                  C.surface,
                alignItems: "center",
                justifyContent:
                  "center",
                padding: 10,
              }}
            >
              <CreatureVisual
                point={TEST_POINT}
                size={64}
              />
            </View>

            <View
              style={{
                flex: 1,
                minWidth: 210,
              }}
            >
              <Text
                style={{
                  color: C.muted,
                  fontSize: 13,
                  fontWeight: "800",
                }}
              >
                {t21.testCreature}
              </Text>

              <Text
                style={{
                  color: C.text,
                  fontSize: 27,
                  lineHeight: 34,
                  fontWeight: "900",
                  marginTop: 6,
                }}
              >
                {statement.title}
              </Text>

              <Text
                style={{
                  color: C.muted,
                  fontSize: 15,
                  lineHeight: 23,
                  marginTop: 7,
                }}
              >
                {statement.body}
              </Text>
            </View>
          </View>

          <ConfidenceBar
            label={
              t21.zorbConfidence
            }
            value={testP}
            accent={C.accent}
          />

          <ConfidenceBar
            label={
              t21.notConfidence
            }
            value={1 - testP}
            accent={C.warning}
          />
        </AIExhibitNarrationPressable>

        <View
          style={{
            flex: 1,
            minWidth: 350,
            padding: 22,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: C.border,
            backgroundColor:
              C.surfaceRaised,
          }}
        >
          <Text
            style={{
              color: C.text,
              fontSize: 19,
              fontWeight: "900",
            }}
          >
            {t21.trainPrompt}
          </Text>

          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 12,
              marginTop: 16,
            }}
          >
            {[
              [
                copy.trainMachine
                  .step,
                model.step,
              ],
              [
                copy.trainMachine
                  .loss,
                metrics.loss.toFixed(
                  3
                ),
              ],
              [
                copy.trainMachine
                  .accuracy,
                `${Math.round(
                  metrics.accuracy *
                    100
                )}%`,
              ],
            ].map(
              ([label, value]) => (
                <View
                  key={label}
                  style={{
                    flex: 1,
                    minWidth: 100,
                  }}
                >
                  <Text
                    style={{
                      color:
                        C.muted,
                      fontSize: 12,
                    }}
                  >
                    {label}
                  </Text>

                  <Text
                    style={{
                      color: C.text,
                      fontSize: 30,
                      fontWeight:
                        "900",
                    }}
                  >
                    {value}
                  </Text>
                </View>
              )
            )}
          </View>

          <AIExhibitNarrationPressable
            audioEnabled={audioEnabled}
            onPress={() =>
              playNarration(
                model.step === 0
                  ? "train_machine_untrained"
                  : model.step < 5
                  ? "train_machine_after_step"
                  : "train_machine_improved"
              )
            }
            style={{
              marginTop: 18,
              padding: 15,
              borderRadius: 14,
              backgroundColor:
                C.surface,
            }}
          >
            <Text
              style={{
                color: model.step
                  ? C.text
                  : C.muted,
                fontSize: 16,
                lineHeight: 24,
              }}
            >
              {model.step === 0
                ? t21.untrainedBody
                : model.step < 5
                ? t21.afterStep
                : t21.improved}
            </Text>
          </AIExhibitNarrationPressable>

          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 10,
              marginTop: 18,
            }}
          >
            <Pressable
              onPress={() =>
                train(1)
              }
              style={({
                pressed,
              }) => ({
                minHeight: 54,
                paddingHorizontal:
                  17,
                paddingVertical:
                  12,
                borderRadius: 14,
                borderWidth: 1,
                borderColor:
                  C.accent,
                backgroundColor:
                  pressed
                    ? C.surface
                    : C.accentSoft,
              })}
            >
              <Text
                style={{
                  color: C.text,
                  fontSize: 16,
                  fontWeight: "900",
                }}
              >
                {
                  copy.trainMachine
                    .oneStep
                }
              </Text>
            </Pressable>

            <Pressable
              onPress={() =>
                train(10)
              }
              style={({
                pressed,
              }) => ({
                minHeight: 54,
                paddingHorizontal:
                  17,
                paddingVertical:
                  12,
                borderRadius: 14,
                borderWidth: 1,
                borderColor:
                  C.accent,
                backgroundColor:
                  pressed
                    ? C.surface
                    : C.accentSoft,
              })}
            >
              <Text
                style={{
                  color: C.text,
                  fontSize: 16,
                  fontWeight: "900",
                }}
              >
                {surpriseAdded
                  ? t21.retrain
                  : copy
                      .trainMachine
                      .tenSteps}
              </Text>
            </Pressable>

            <Pressable
              onPress={reset}
              style={({
                pressed,
              }) => ({
                minHeight: 54,
                paddingHorizontal:
                  17,
                paddingVertical:
                  12,
                borderRadius: 14,
                borderWidth: 1,
                borderColor:
                  C.border,
                backgroundColor:
                  pressed
                    ? C.accentSoft
                    : C.surface,
              })}
            >
              <Text
                style={{
                  color: C.text,
                  fontSize: 16,
                  fontWeight: "900",
                }}
              >
                ↻{" "}
                {
                  copy.trainMachine
                    .reset
                }
              </Text>
            </Pressable>
          </View>

          <Pressable
            onPress={() => {
              const nextShowInside = !showInside;
              setShowInside(nextShowInside);
              if (nextShowInside) {
                playNarration("train_machine_inside_parameters");
              }
            }}
            style={{
              marginTop: 18,
              alignSelf:
                "flex-start",
            }}
          >
            <Text
              style={{
                color: C.accent,
                fontSize: 15,
                fontWeight: "900",
              }}
            >
              {showInside
                ? "▾"
                : "▸"}{" "}
              {t21.insideTitle}
            </Text>
          </Pressable>

          {showInside ? (
            <View
              style={{
                marginTop: 10,
                padding: 14,
                borderRadius: 13,
                borderWidth: 1,
                borderColor:
                  C.border,
                backgroundColor:
                  C.surface,
              }}
            >
              <Text
                style={{
                  color: C.muted,
                  fontSize: 14,
                  lineHeight: 21,
                }}
              >
                {t21.insideBody}
              </Text>

              <ParameterRow
                label={
                  copy
                    .trainMachine
                    .weightA
                }
                value={model.w1}
              />

              <ParameterRow
                label={
                  copy
                    .trainMachine
                    .weightB
                }
                value={model.w2}
              />

              <ParameterRow
                label={
                  copy
                    .trainMachine
                    .bias
                }
                value={model.b}
              />

              <Text
                style={{
                  color: C.muted,
                  fontSize: 12,
                  lineHeight: 18,
                  marginTop: 12,
                }}
              >
                {t21.mathSecondary}
              </Text>
            </View>
          ) : null}
        </View>
      </View>

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 18,
        }}
      >
        {copy.trainMachine.cycle.map(
          (item) => (
            <View
              key={item}
              style={{
                flex: 1,
                minWidth: 190,
                padding: 15,
                borderRadius: 15,
                borderWidth: 1,
                borderColor:
                  C.border,
                backgroundColor:
                  C.surfaceRaised,
              }}
            >
              <Text
                style={{
                  color: C.text,
                  fontSize: 16,
                  fontWeight: "900",
                  textAlign: "center",
                }}
              >
                {item}
              </Text>
            </View>
          )
        )}
      </View>

      {model.step >= 5 ? (
        <View
          style={{
            marginTop: 20,
            padding: 22,
            borderRadius: 20,
            borderWidth: 1,
            borderColor:
              surpriseVisible
                ? C.warning
                : C.border,
            backgroundColor:
              C.surfaceRaised,
          }}
        >
          {!surpriseVisible ? (
            <>
              <Text
                style={{
                  color: C.text,
                  fontSize: 22,
                  fontWeight: "900",
                }}
              >
                {language === "es"
                  ? "¿La máquina aprendió la regla correcta?"
                  : "Did the machine learn the right rule?"}
              </Text>

              <Text
                style={{
                  color: C.muted,
                  fontSize: 16,
                  lineHeight: 24,
                  marginTop: 8,
                }}
              >
                {language === "es"
                  ? "Los ejemplos originales podrían haber permitido un atajo. Probemos algo que la máquina nunca vio."
                  : "The original examples may have allowed a shortcut. Let's try something the machine never saw."}
              </Text>

              <Pressable
                onPress={() => {
                  setSurpriseVisible(true);
                  playNarration(
                    surprisePredictsZorb
                      ? "train_machine_surprise_fooled"
                      : "train_machine_surprise_uncertain"
                  );
                }}
                style={({
                  pressed,
                }) => ({
                  marginTop: 16,
                  minHeight: 54,
                  alignSelf:
                    "flex-start",
                  paddingHorizontal:
                    19,
                  paddingVertical:
                    12,
                  borderRadius: 14,
                  borderWidth: 1,
                  borderColor:
                    C.warning,
                  backgroundColor:
                    pressed
                      ? C.surface
                      : C.accentSoft,
                })}
              >
                <Text
                  style={{
                    color: C.text,
                    fontSize: 16,
                    fontWeight:
                      "900",
                  }}
                >
                  ⚡{" "}
                  {
                    t21.surpriseButton
                  }
                </Text>
              </Pressable>
            </>
          ) : (
            <>
              <Text
                style={{
                  color: C.warning,
                  fontSize: 13,
                  fontWeight: "900",
                  letterSpacing: 0.8,
                }}
              >
                {language === "es"
                  ? "NUEVO EJEMPLO"
                  : "NEW EXAMPLE"}
              </Text>

              <Text
                style={{
                  color: C.text,
                  fontSize: 25,
                  lineHeight: 32,
                  fontWeight: "900",
                  marginTop: 7,
                }}
              >
                {t21.surpriseTitle}
              </Text>

              <View
                style={{
                  flexDirection:
                    "row",
                  flexWrap: "wrap",
                  gap: 18,
                  alignItems:
                    "center",
                  marginTop: 16,
                }}
              >
                <CreatureCard
                  point={
                    SURPRISE_POINT
                  }
                  label={
                    t21.surpriseCorrect
                  }
                  highlight
                />

                <View
                  style={{
                    flex: 2,
                    minWidth: 300,
                  }}
                >
                  <Text
                    style={{
                      color:
                        C.muted,
                      fontSize: 15,
                      lineHeight: 23,
                    }}
                  >
                    {
                      t21.surpriseBefore
                    }
                  </Text>

                  <Text
                    style={{
                      color:
                        surprisePredictsZorb
                          ? C.warning
                          : C.success,
                      fontSize: 24,
                      lineHeight: 31,
                      fontWeight:
                        "900",
                      marginTop: 10,
                    }}
                  >
                    {language ===
                    "es"
                      ? "La máquina dice:"
                      : "Machine says:"}{" "}
                    {surprisePredictsZorb
                      ? copy
                          .trainMachine
                          .zorb
                      : copy
                          .trainMachine
                          .notZorb}{" "}
                    (
                    {Math.round(
                      (surprisePredictsZorb
                        ? surpriseP
                        : 1 -
                          surpriseP) *
                        100
                    )}
                    %)
                  </Text>

                  <Text
                    style={{
                      color: C.text,
                      fontSize: 16,
                      lineHeight: 25,
                      marginTop: 10,
                    }}
                  >
                    {surprisePredictsZorb
                      ? t21.surpriseFooled
                      : t21.surpriseNotFooled}
                  </Text>
                </View>
              </View>

              {!surpriseAdded ? (
                <Pressable
                  onPress={() => {
                    setSurpriseAdded(true);
                    playNarration("train_machine_surprise_added");
                  }}
                  style={({
                    pressed,
                  }) => ({
                    marginTop: 18,
                    minHeight: 54,
                    alignSelf:
                      "flex-start",
                    paddingHorizontal:
                      19,
                    paddingVertical:
                      12,
                    borderRadius: 14,
                    borderWidth: 1,
                    borderColor:
                      C.accent,
                    backgroundColor:
                      pressed
                        ? C.surface
                        : C.accentSoft,
                  })}
                >
                  <Text
                    style={{
                      color: C.text,
                      fontSize: 16,
                      fontWeight:
                        "900",
                    }}
                  >
                    +{" "}
                    {t21.addSurprise}
                  </Text>
                </Pressable>
              ) : (
                <View
                  style={{
                    marginTop: 18,
                    padding: 16,
                    borderRadius: 14,
                    borderLeftWidth: 5,
                    borderWidth: 1,
                    borderColor:
                      C.border,
                    borderLeftColor:
                      C.success,
                    backgroundColor:
                      C.accentSoft,
                  }}
                >
                  <Text
                    style={{
                      color: C.text,
                      fontSize: 16,
                      lineHeight: 25,
                    }}
                  >
                    {surpriseP <
                      0.5 &&
                    model.step >= 10
                      ? t21.learnedFromSurprise
                      : t21.surpriseAdded}
                  </Text>
                </View>
              )}
            </>
          )}
        </View>
      ) : null}

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("train_machine_model_caveat")}
        style={{
          marginTop: 18,
          padding: 16,
          borderRadius: 15,
          borderWidth: 1,
          borderColor: C.border,
          backgroundColor:
            C.surfaceRaised,
        }}
      >
        <Text
          style={{
            color: C.muted,
            fontSize: 15,
            lineHeight: 23,
          }}
        >
          {copy.trainMachine.caveat}
        </Text>
      </AIExhibitNarrationPressable>

      <Pressable
        onPress={() =>
          navigate("sentiment")
        }
        style={({ pressed }) => ({
          marginTop: 20,
          alignSelf: "flex-start",
          minHeight: 58,
          paddingHorizontal: 22,
          paddingVertical: 14,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: C.accent,
          backgroundColor: pressed
            ? C.surfaceRaised
            : C.accentSoft,
        })}
      >
        <Text
          style={{
            color: C.text,
            fontSize: 17,
            fontWeight: "900",
          }}
        >
          {
            copy.trainMachine
              .toSentiment
          }{" "}
          →
        </Text>
      </Pressable>
    </View>
  );
}
